/**
 * FE-OV-POLL-1 — overview live poll degraded detection + banner (DEF-FE-OV-POLL-001).
 */

import { hasUsableOverviewGroupSlots } from "./overview-group-slots.js";

/** Min ms between live-poll failure toasts (6s tick → max ~1/min). */
export const OVERVIEW_LIVE_POLL_TOAST_COOLDOWN_MS = 60000;

/**
 * @param {{ ov?: object | null, list?: object | null } | null | undefined} tick
 * @returns {{ degraded: boolean, partial: boolean, message: string }}
 */
export function assessOverviewLiveTick(tick) {
  const ovOk = !!(tick && tick.ov && typeof tick.ov === "object");
  const listOk = !!(tick && tick.list && typeof tick.list === "object");
  if (ovOk && listOk) {
    return { degraded: false, partial: false, message: "" };
  }
  if (!ovOk && !listOk) {
    return {
      degraded: true,
      partial: false,
      message: "Overview live refresh failed — showing last loaded data.",
    };
  }
  if (!ovOk) {
    return {
      degraded: true,
      partial: true,
      message: "Overview counts could not refresh — numbers may be outdated.",
    };
  }
  // CAP-API-1a: overview + notification_group_slots already drive group cards.
  // Device sample is best-effort enrichment — do not banner/toast when slots are present.
  if (hasUsableOverviewGroupSlots(tick.ov)) {
    return { degraded: false, partial: true, message: "" };
  }
  return {
    degraded: true,
    partial: true,
    message: "Device list could not refresh — group cards may show incomplete device lists.",
  };
}

/**
 * @param {{ degraded?: boolean, message?: string }} status
 * @param {string} [bannerId]
 * @returns {string}
 */
export function overviewLivePollDegradedBannerHtml(status, bannerId = "ovLivePollDegradedBanner") {
  if (!status || !status.degraded) return "";
  const msg = String(status.message || "Overview refresh incomplete.").trim();
  return (
    `<p class="badge offline" id="${bannerId}" data-overview-live-degraded="1" style="margin:8px 0">` +
    `${msg} Retrying automatically.` +
    `</p>`
  );
}

/**
 * @param {ParentNode | null | undefined} view
 * @param {{ degraded?: boolean, message?: string }} status
 * @param {string} [bannerId]
 */
export function syncOverviewLivePollDegradedBanner(view, status, bannerId = "ovLivePollDegradedBanner") {
  if (!view) return;
  const existing = view.querySelector(`#${bannerId}`);
  const html = overviewLivePollDegradedBannerHtml(status, bannerId);
  if (!html) {
    if (existing) existing.remove();
    return;
  }
  if (existing) {
    if (existing.outerHTML !== html) existing.outerHTML = html;
    return;
  }
  const anchor = view.querySelector("header.page-head, section.card, .page-head");
  if (anchor) anchor.insertAdjacentHTML("afterend", html);
  else view.insertAdjacentHTML("afterbegin", html);
}

/**
 * @param {number} lastToastAt
 * @param {{ degraded?: boolean, message?: string }} status
 * @param {number} [cooldownMs]
 * @returns {{ toast: boolean, at: number }}
 */
export function shouldToastOverviewLivePollFail(lastToastAt, status, cooldownMs = OVERVIEW_LIVE_POLL_TOAST_COOLDOWN_MS) {
  if (!status || !status.degraded) return { toast: false, at: lastToastAt };
  const now = Date.now();
  const cd = Number.isFinite(Number(cooldownMs)) ? Number(cooldownMs) : OVERVIEW_LIVE_POLL_TOAST_COOLDOWN_MS;
  if (now - lastToastAt < cd) return { toast: false, at: lastToastAt };
  return { toast: true, at: now };
}
